import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '@services/auth.service';
import { finalize } from 'rxjs';

export interface ForgotPasswordDialogData {
  /**
   * Opened by a sign-in the server answered with a reset (an expired password, or a lapsed lockout
   * that requires one), not by the link. Which rule applied is not said, so the copy serves both.
   */
  resetRequired?: boolean;
}

/**
 * Forgot password: one field, and one answer whatever it matched (openspec
 * stop-emailing-recoverable-passwords). The server answers every request the same way, so there is
 * nothing to tell the user but that the request went out; only a refusal to take it is shown.
 */
@Component({
  selector: 'app-forgot-password-dialog',
  templateUrl: './forgot-password-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordDialogComponent {
  readonly titleId = 'forgot-password-title';

  readonly form = new FormGroup({
    identifier: new FormControl('', { nonNullable: true }),
  });

  /** The request went out: the dialog shows the one answer every request gets. */
  sent = false;
  sending = false;
  /** A catalog key, translated in the template. */
  sendError = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: ForgotPasswordDialogData | null,
    readonly dialogRef: MatDialogRef<ForgotPasswordDialogComponent>,
    private _as: AuthService,
    private _cdr: ChangeDetectorRef,
  ) {}

  submit() {
    const control = this.form.controls.identifier;
    const identifier = control.value.trim();
    this.sendError = '';

    if (!identifier) {
      control.setErrors({ required: true });
      control.markAsTouched();
      return;
    }

    this.sending = true;
    this._as
      .resetUserPassword(identifier)
      .pipe(
        finalize(() => {
          this.sending = false;
          this._cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => (this.sent = true),
        error: (error: HttpErrorResponse) => {
          this.sendError =
            error.status === 429 ? 'ForgotPassword.TooManyRequests' : 'ForgotPassword.SendFailed';
        },
      });
  }
}
